import { SelectChangeEvent } from "@mui/material";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteRoleById, getAllOrganizations, getRolesByOrgId } from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { RolesContextProps } from "../../interfaces/SuperAdmin/ContextProps/iRoles";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { iDataRoles } from "../../interfaces/SuperAdmin/Pages/iDataRoles";

const RolesContext = createContext<RolesContextProps>({} as RolesContextProps);

interface Props {
  children: React.ReactNode;
}

const RolesProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();
  const { canAccess } = useAuthContext();

  const [organizaciones, setOrganizaciones] = useState<iDataOrganizacion[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    idOrganization
  );
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [roles, setRoles] = useState<iDataRoles[]>([]);

  const navigate = useNavigate();

  // Fetch de organizaciones para seleccionar
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgsRes = await getAllOrganizations();
      if (orgsRes.data.statusCode === 200) {
        console.log(orgsRes.data.body);

        setOrganizaciones(orgsRes.data.body);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  // Lo hacemos al montar el componente SOLO si es un SuperAdmin
  useEffect(() => {
    const isSuperAdmin = canAccess("view:roles");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  // Fetch para traer los roles de la organizacións seleccionada
  useEffect(() => {
    const fetchRoles = async (id: string) => {
      setIsLoadingRoles(true);
      try {
        const res = await getRolesByOrgId(id);
        if (res.data.statusCode === 200) {
          setRoles(res.data.body);
        } else {
          toast.error("Error al obtener los roles de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los roles de la organización");
      } finally {
        setIsLoadingRoles(false);
      }
    };

    if (selectedOrg) {
      fetchRoles(selectedOrg);
    }
  }, [selectedOrg]);

  // Funcion para cambiar de organización
  const handleSelectChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    navigate(`/asistente/roles/${event.target.value}`);
  };

  //Funcion para abrir el modal
  const openModal = (id: string) => {
    setIsModalOpen(true);
    setSelectedRole(id);
  };

  //Funcion para cerrar el modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
  };

  // Borrar rol
  const deleteRole = async (id: string) => {
    setIsDeleting(true);
    try {
      const res = await deleteRoleById(id);
      console.log(res);
      if (res.data.statusCode === 200) {
        setRoles((prev) => prev.filter((item) => item.id_rol !== id));
        closeModal();
        toast.success("Rol eliminado exitosamente");
      } else {
        toast.error("Error al eliminar el rol");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar el rol");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <RolesContext.Provider
      value={{
        organizaciones,
        selectedOrg,
        isLoadingOrgs,
        isLoadingRoles,
        roles,
        isDeleting,
        isModalOpen,
        selectedRole,
        handleSelectChange,
        openModal,
        closeModal,
        deleteRole,
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};

const useSuperAdminRoles = () => useContext(RolesContext);

export { RolesProvider, useSuperAdminRoles };

